import React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  alpha,
  Box,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Spell } from '../../types';
import { SpellSchoolChip } from './SpellSchoolChip';
import { createParchmentSurface, hashStringToSeed, parchmentUnderlineSx } from '../../theme/surfaces';
import { ConfirmActionDialog } from './ConfirmActionDialog';

interface SpellCardProps {
  spell: Spell;
  onDelete?: (spell: Spell) => void;
  deleteDisabled?: boolean;
  unusedLevelUps?: number;
  onImprove?: (spell: Spell) => Promise<void>;
  showTargetNumber?: boolean;
}

export const SpellCard = React.memo(function SpellCard({
  spell,
  onDelete,
  deleteDisabled = false,
  unusedLevelUps,
  onImprove,
  showTargetNumber = true,
}: SpellCardProps) {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [isImproveConfirmOpen, setIsImproveConfirmOpen] = React.useState(false);
  const [isImproving, setIsImproving] = React.useState(false);
  const hasActions = Boolean(onDelete);
  const canImprove = !deleteDisabled && (unusedLevelUps ?? 0) > 0 && Boolean(onImprove);
  const parchmentSx = React.useMemo(() => createParchmentSurface(hashStringToSeed(spell.id)), [spell.id]);

  const handleImproveConfirm = async () => {
    if (!onImprove) return;
    setIsImproving(true);
    try {
      await onImprove(spell);
    } finally {
      setIsImproving(false);
      setIsImproveConfirmOpen(false);
    }
  };

  return (
    <Card elevation={0} sx={{ ...parchmentSx, filter: 'drop-shadow(0 3px 6px rgba(40, 20, 5, 0.25))' }}>
      <CardContent sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 1, height: '100%' }}>
          <Stack direction="column">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, ...parchmentUnderlineSx }}>
                {spell.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canImprove && (
                  <IconButton
                    aria-label={`Improve ${spell.name}`}
                    size="small"
                    onClick={() => setIsImproveConfirmOpen(true)}
                    disabled={isImproving}
                    sx={{ backgroundColor: alpha("#FFD700", 0.55), ":hover": { backgroundColor: alpha("#FFD700", 0.75) } }}
                  >
                    <FileUploadIcon fontSize="small" />
                  </IconButton>
                )}
                {hasActions && (
                  <>
                    <IconButton
                      aria-label={`Spell actions for ${spell.name}`}
                      size="small"
                      onClick={(event) => setMenuAnchor(event.currentTarget)}
                      disabled={deleteDisabled}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                      <MenuItem
                        onClick={() => { setMenuAnchor(null); onDelete?.(spell); }}
                        disabled={deleteDisabled}
                      >
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Erase
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 1, overflow: 'auto' }}>
              {spell.description?.trim() || 'No description available.'}
            </Typography>
          </Stack>
          <Stack direction="column" gap={1}>
            <Typography variant="body2" color="textSecondary">
              Categories:{' '}
              {spell.categories.length > 0
                ? spell.categories.map((category) => category.description).join(', ')
                : 'None'}
            </Typography>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }} width="100%">
              <SpellSchoolChip label={spell.school} />
              {showTargetNumber && (
                <Typography variant="subtitle1" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                  {spell.castingNumber}  ({spell.castingNumber + 2})
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>

      </CardContent>
      {canImprove && (
        <ConfirmActionDialog
          open={isImproveConfirmOpen}
          title="Improve Spell"
          message={`Spend a level-up to improve ${spell.name}? Its casting number will decrease from ${spell.castingNumber} to ${spell.castingNumber - 1}.`}
          confirmLabel="Improve"
          busy={isImproving}
          onClose={() => { if (!isImproving) setIsImproveConfirmOpen(false); }}
          onConfirm={handleImproveConfirm}
        />
      )}
    </Card>
  );
});
